import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { StatAlertsStatusSelector } from 'app/components/form-controls/stat-alerts-status-selector';
import { GeneralConstants } from 'app/constants/general-constants';
import { StatAlertsStatus } from 'app/enum/stat-alerts';
import { Comment } from 'app/model/comment';
import { statAlertCommentService } from 'app/services/comment.service';
import { statAlertsService } from 'app/services/seam-request/stat-alerts.service';

import { CommentGrid } from './stat-alert-comment-grid';

interface StatAlertDialogProps {
    open: boolean;
    alertId: number;
    status: StatAlertsStatus;
    onClose: (id: number, status: StatAlertsStatus, withConfirmation: boolean) => void;
}

export const formatIntAsUUID = (input: number): string => {
    const lastBitCount = 12;

    return `07786dfe-6571-4589-af3b-${String(input).padStart(lastBitCount, '0')}`;
};

export const StatAlertDialog: React.FunctionComponent<StatAlertDialogProps> = (props: StatAlertDialogProps) => {
    const { open, alertId, status } = props;
    const [comment, setComment] = useState<Comment[]>([]);
    const [diagStatus, setDiagStatus] = useState(status);
    const [diagComment, setDiagComment] = useState('');

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const comRefId = formatIntAsUUID(alertId);
        const parent = { id: comRefId };
        // const parent = {"id" : '92b7dcea-d247-49f4-bad6-16682d42f826'};
        // Since StatAlertDialog is loaded once, diagStatus has stale value.
        // Update it to current value
        setDiagStatus(status);
        setDiagComment('');
        const commentList = statAlertCommentService.listAll({}, parent).subscribe((c) => {
            setComment(c);
        });

        // .subscribe(setComment);
        return () => {
            commentList.unsubscribe();
        };
    }, [alertId, open]);

    const handleSubmit = () => {
        // Save status if changed from original status
        if (status !== diagStatus) {
            statAlertsService.updateStatus(alertId, diagStatus as number);
            // Save comment if not empty
            if ('' !== diagComment) {
                const comRefId = formatIntAsUUID(alertId);
                const parent = { id: comRefId };
                const saveComment = { comment: diagComment };
                statAlertCommentService.save(saveComment, parent).subscribe();
            }
        }
        // Bubble up to update the grid
        props.onClose(alertId, diagStatus, true);
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth='lg'
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
            onClose={() => {
                props.onClose(alertId, diagStatus, false);
            }}
        >
            <DialogTitle id='alert-dialog-title'> {'Update Status'} </DialogTitle>
            <div>
                <DialogContent>
                    <Grid item xs={6}>
                        <StatAlertsStatusSelector
                            label='Status:'
                            value={diagStatus}
                            emptyLabel='(any)'
                            onChange={(evt) => {
                                setDiagStatus(evt.target.value as StatAlertsStatus);
                            }}
                        />
                        <TextField
                            fullWidth
                            name='comment'
                            label='Comment'
                            id='standard-basic'
                            variant='standard'
                            multiline
                            onBlur={(evt) => {
                                setDiagComment(evt.target.value);
                            }}
                        />
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmit}>Update Status</Button>
                </DialogActions>
            </div>
            <CommentGrid
                rows={comment}
                density='compact'
                pagination
                autoHeight
                pageSizeOptions={GeneralConstants.PAGE_SIZE_OPTIONS}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                    pagination: {
                        paginationModel: {
                            pageSize: GeneralConstants.PAGE_SIZE_SMALL,
                        },
                    },
                }}
            />
        </Dialog>
    );
};
